import React, { useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

/**
 * SectionReorderModal
 * -------------------
 * Reordering a whole deck, as a numbered list.
 *
 * The drawer can already reorder sections two ways — dragging a handle, and the
 * arrows on each header — but both work a section at a time inside a 300px
 * column that also holds every slide, so rearranging a deck of any size means
 * scrolling between each move and losing your place. This puts every section on
 * one screen, in order, numbered, with nothing else competing for the space.
 *
 * Deliberately not drag-and-drop. Drag is what makes the drawer awkward in the
 * first place; a list of buttons is unambiguous, works from the keyboard, and
 * cannot half-complete.
 *
 * Moves are applied straight through to the deck rather than staged and
 * committed, so the drawer behind the modal reorders as you go and there is no
 * second copy of the order to keep in sync.
 */
const SectionReorderModal = ({ isOpen, sections = [], onMove, onClose, labelFor }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Focus moves into the dialog on open, so the keyboard is already here and
  // Escape works without clicking first.
  useEffect(() => {
    if (isOpen) closeRef.current?.focus();
  }, [isOpen]);

  const handleBackdrop = useCallback((event) => {
    if (event.target === event.currentTarget) onClose?.();
  }, [onClose]);

  if (!isOpen) return null;

  const nameOf = (section, index) =>
    (labelFor ? labelFor(section) : section.title) || `Section ${index + 1}`;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={handleBackdrop}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="section-reorder-title"
        className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-700 px-5 py-4">
          <div>
            <h2 id="section-reorder-title" className="text-base font-bold text-white">
              Reorder sections
            </h2>
            <p className="mt-0.5 text-xs text-gray-400">
              The deck runs top to bottom. Slides move with their section.
            </p>
          </div>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-700 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <ol className="flex-1 overflow-y-auto px-3 py-3">
          {sections.length === 0 ? (
            <li className="px-2 py-6 text-center text-sm text-gray-400">
              This deck has no sections yet.
            </li>
          ) : (
            sections.map((section, index) => (
              <li
                key={section.id}
                className="mb-1.5 flex items-center gap-3 rounded-xl border border-gray-700 bg-gray-800 px-3 py-2.5 last:mb-0"
              >
                {/* The position is the point of this list, so it is stated
                    rather than implied by an <ol> marker that a reader would
                    have to hunt for against a dark panel. */}
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-700 text-xs font-bold text-gray-200">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-gray-100">
                    {nameOf(section, index)}
                  </span>
                  <span className="block text-[11px] text-gray-400">
                    {(section.blocks?.length || 0) === 1
                      ? '1 block'
                      : `${section.blocks?.length || 0} blocks`}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onMove?.(section.id, -1)}
                    disabled={index === 0}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-30 disabled:hover:bg-gray-700"
                    aria-label={`Move ${nameOf(section, index)} up`}
                    title="Move up"
                  >
                    <ChevronUp size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove?.(section.id, 1)}
                    disabled={index === sections.length - 1}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-30 disabled:hover:bg-gray-700"
                    aria-label={`Move ${nameOf(section, index)} down`}
                    title="Move down"
                  >
                    <ChevronDown size={15} />
                  </button>
                </span>
              </li>
            ))
          )}
        </ol>

        <div className="flex justify-end border-t border-gray-700 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionReorderModal;
